import util from "@utils/utils";
import { useState, useEffect, useRef } from 'react';
import { MindTree } from '../MindTree';
import { Timeline } from "../Timeline";
import { MarkBtn } from '../MarkBtn';
import { Activity, ChevronRight, ChevronsUp, ChevronsDown, Star, Printer, Download, Clock, Plus, Check, BookOpen } from 'react-feather';
import { useStore } from '../../StoreContext';
import { useAnimation } from '@components/AnimationProvider/index.jsx';
import { useNavigate } from 'react-router-dom';
import { checkLoginAndExecute } from '@utils/authUtils';

export const FileCard = (props) => {
    const { input, getPdfAction, type, index, pageSize, pageNum, item, fileName, setMdTxt, setFileName, setFileId, setMarkData, savedFoldListData, mindmapVisible, hightlight, score, noClass, noSource, noSummary, handleLoginRequired } = props
    const [mindMapCollapsed, setMindMapCollapsed] = useState(index === 0 ? false : true)
    const [timelineCollapsed, setTimelineCollapsed] = useState(true)
    const [jieduCollapsed, setJieduCollapsed] = useState(true)
    const { triggerAnimation } = useAnimation();
    const navigate = useNavigate();
    const cardRef = useRef(null);

    // 使用安全的URL构建方法
    const filePage = util.buildPolicyFileUrl(
        item?.policy_title || item?.policy_name,
        item?.source_id,
        input || '',
        window.location.href
    );

    const { state, actions } = useStore();

    // ✅ 添加缺失的renderTags函数
    const renderTags = (tags) => {
        if (!tags) return null;
        
        // 如果tags是字符串，按逗号分割
        const tagArray = typeof tags === 'string' ? tags.split(',').filter(tag => tag.trim()) : (Array.isArray(tags) ? tags : []);
        
        return (
            <div className="flex flex-wrap gap-1">
                {tagArray.map((tag, index) => (
                    <span key={index} className="badge badge-outline badge-sm">
                        {tag.trim()}
                    </span>
                ))}
            </div>
        );
    };
    
    // ✅ 添加缺失的shouldShowJieduButton变量
    // 判断是否显示相关解读按钮
    // 如果policy_class为"政策解读"，则隐藏相关解读按钮
    const shouldShowJieduButton = item?.policy_class !== '文件解读';

    // ✅ 使用统一的登录检查函数
    const executeWithLoginCheck = (action, options = {}) => {
        return checkLoginAndExecute(action, options);
    };
    
    // 处理需要登录的下载操作
    const handleDownloadClick = (e) => {
        e.stopPropagation();
        executeWithLoginCheck(() => {
            getPdfAction({ policy_info: [{ policy_title: item?.policy_title || item?.policy_name, policy_id: item?.source_id }] });
        });
    };

    // 处理需要登录的对比操作
    const handleCompareClick = (e) => {
        e.stopPropagation();
        executeWithLoginCheck(() => {
            if (!state?.some(i => i?.source_id === item?.source_id)) {
                actions.updateValue([item, ...state])
            }
            navigate(`/policy/compareSelect?clickFrom=${item?.source_id}&returnUrl=${encodeURIComponent(window.location.href)}`);
        });
    };
    
    // 处理原文查看
    const handleViewOriginal = () => {
        executeWithLoginCheck(() => {
            setFileName(item?.policy_title || item?.policy_name);
            window.open(filePage, '_blank');
        });
    };

    // 处理文章脉络展开，包含滚动位置管理
    const handleMindMapToggle = (e) => {
        e.stopPropagation();

        // 如果当前是折叠状态，即将展开
        if (mindMapCollapsed) {
            // 在展开前记录按钮的当前位置
            const buttonElement = e.currentTarget;
            const buttonRect = buttonElement.getBoundingClientRect();
            const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const buttonAbsoluteTop = buttonRect.top + currentScrollTop;

            // 展开文章脉络
            setMindMapCollapsed(false);
            setTimelineCollapsed(true);
            setJieduCollapsed(true);

            // 使用更长的延迟确保DOM完全更新
            setTimeout(() => {
                // 重新计算按钮位置（因为展开可能改变了布局）
                const newButtonRect = buttonElement.getBoundingClientRect();
                const newScrollTop = window.pageYOffset || document.documentElement.scrollTop;
                const newButtonAbsoluteTop = newButtonRect.top + newScrollTop;

                // 计算合适的滚动位置：按钮应该在视窗上方，留出空间给展开的内容
                const headerOffset = 100; // 为固定头部留出空间
                const targetScrollTop = Math.max(0, newButtonAbsoluteTop - headerOffset);

                // 只有当需要滚动时才执行滚动
                if (Math.abs(currentScrollTop - targetScrollTop) > 50) {
                    window.scrollTo({
                        top: targetScrollTop,
                        behavior: 'smooth'
                    });
                }
            }, 300); // 增加延迟时间确保MindTree完全渲染
        } else {
            // 简单折叠
            setMindMapCollapsed(true);
        }
    };
    
    return (
        <>
            {
                type === 'row' ?
                    <>
                        <td>
                            <span className="hover:text-primary cursor-pointer font-bold" onClick={handleViewOriginal}>
                                {item?.policy_title || item?.policy_name}
                            </span>
                        </td>
                        <td>
                            {item?.from_organization?.replaceAll(/:/g, "")}
                        </td>
                        <td>
                            {renderTags(item?.policy_theme)}
                        </td>
                        <td>
                            <span className={`font-[600] ${item?.score > 90 ? 'text-orange-600 saturate-80' : item?.score > 80 ? 'text-primary saturate-80' : ''}`}>
                                {(item?.score)?.toFixed(2)}
                            </span>
                        </td>
                        <td>
                            <span className="whitespace-nowrap">
                                {util.dateTransformToCN(item?.policy_time)}
                            </span>
                        </td>
                        <td>
                            <div className="flex justify-center items-center">
                                <button className="btn btn-ghost btn-sm btn-secondary" onClick={() => {
                                    executeWithLoginCheck(() => {
                                        setFileName(item?.policy_title || item?.policy_name); 
                                        setFileId(item?.source_id);
                                        window.open(filePage, '_blank');
                                    });
                                }}>
                                    <ChevronRight size="15" />
                                </button>
                                <button className="btn btn-ghost btn-sm btn-secondary"
                                    onClick={handleDownloadClick}
                                >
                                    <Download size="15" />
                                </button>
                                <MarkBtn
                                    savedFoldListData={savedFoldListData}
                                    source_id={item?.source_id}
                                    setMarkData={setMarkData}
                                    type={type}
                                    handleLoginRequired={executeWithLoginCheck}
                                />
                            </div>
                        </td>
                    </>
                    :
                    <>
                        <div ref={cardRef} className="flex justify-between items-start mb-2">
                            <div>
                                <span className="hover:text-primary text-2xl font-bold cursor-pointer inline" onClick={(e) => {
                                    e.stopPropagation();
                                    executeWithLoginCheck(() => {
                                        setFileName(item?.policy_title || item?.policy_name);
                                        window.open(filePage, '_blank');
                                    });
                                }}>
                                    {((pageNum ?? 1) - 1) * (pageSize ?? 0) + index + 1} .
                                    {hightlight && item?.high_light_index && item?.high_light_index?.length > 0 && item?.high_light_index?.some(item => item?.end_position === 0) ?
                                        <span className="bg-yellow-200">{(item?.policy_title || item?.policy_name)?.replace('[解读]', '')?.replace('[重要讲话]', '')}</span> :
                                        (item?.policy_title || item?.policy_name)?.replace('[解读]', '')?.replace('[重要讲话]', '')}
                                </span>
                            </div>
                            {item?.score && score && <span>
                                <span className='text-sm text-gray-500'>相关度：</span>
                                <span className={`font-[600] ${item?.score > 90 ? 'text-orange-600 saturate-80' : item?.score > 80 ? 'text-primary saturate-80' : ''}`}>
                                    {(item?.score)?.toFixed(2)}
                                </span>
                            </span>}
                        </div>
                        <div className="card-actions justify-start gap-2">
                            {!noClass && <div className="text-gray-500">[{item.large_policy_class}]</div>}
                            {!noSource && <div className="text-gray-500">政策来源：{item?.from_organization?.replaceAll(/:/g, "")}</div>}
                            <div className="text-gray-500">{util.dateTransformToCN(item?.policy_time)}</div>
                        </div>
                        {!noSummary &&
                            <>
                                {hightlight && item?.high_light_index && item?.high_light_index?.length > 0 ?
                                    <div dangerouslySetInnerHTML={{ __html: util.HighlightText(item?.related_content, item?.high_light_index?.filter(item => item?.end_position !== 0), true) }}>
                                    </div>
                                    :
                                    <span className="truncate-three-lines">{item?.related_content}</span>
                                }
                            </>
                        }
                        <div className="card-actions justify-between items-center">
                            <div>
                                {renderTags(item?.policy_theme)}
                            </div>
                        </div>
                        <div className="card-actions justify-end items-center gap-1">
                            {/* 首页不显示相关解读按钮 */}
                            {mindmapVisible && shouldShowJieduButton && input && <button className={`btn btn-ghost btn-sm btn-secondary text-primary ${jieduCollapsed ? '' : 'btn-active'}`} onClick={(e) => {
                                e.stopPropagation();
                                setJieduCollapsed(!jieduCollapsed); setMindMapCollapsed(true); setTimelineCollapsed(true);
                            }}>
                                <BookOpen size="15" />
                                相关解读
                            </button>}

                            {mindmapVisible && <button className={`btn btn-ghost btn-sm btn-secondary text-primary ${mindMapCollapsed ? '' : 'btn-active'}`} onClick={handleMindMapToggle}>
                                <Activity size="15" />
                                文章脉络
                            </button>}
                            {mindmapVisible && <button className={`btn btn-ghost btn-sm btn-secondary text-primary ${timelineCollapsed ? '' : 'btn-active'}`} onClick={(e) => {
                                e.stopPropagation();
                                setTimelineCollapsed(!timelineCollapsed); setMindMapCollapsed(true); setJieduCollapsed(true);
                            }}>
                                <Clock size="15" />
                                政策时间线分析
                            </button>}

                            <button className="btn btn-ghost btn-sm btn-secondary text-primary" onClick={handleCompareClick}>
                                <Plus size="15" />
                                政策对比
                            </button>

                            <button className="btn btn-ghost btn-sm btn-secondary" onClick={(e) => {
                                e.stopPropagation();
                                executeWithLoginCheck(() => {
                                    setFileName(item?.policy_title || item?.policy_name);
                                    setFileId(item?.source_id);
                                    window.open(filePage, '_blank');
                                });
                            }}>
                                <ChevronRight size="15" />
                                原文
                            </button>

                            <button className="btn btn-ghost btn-sm btn-secondary" onClick={handleDownloadClick}>
                                <Download size="15" />
                                下载
                            </button>
                            <MarkBtn
                                savedFoldListData={savedFoldListData}
                                source_id={item?.source_id}
                                setMarkData={setMarkData}
                                type={type}
                                handleLoginRequired={executeWithLoginCheck}
                            />
                        </div>
                        {!mindMapCollapsed && <div className={`bg-[#fafafa] rounded-box`} onClick={(e) => e.stopPropagation()}>
                            <MindTree
                                fileName={item?.policy_title || item?.policy_name}
                                fileId={item?.source_id}
                                setFileName={setFileName}
                                setMdTxt={setMdTxt}
                            />
                        </div>}
                        <div className={`bg-[#fafafa] rounded-box ${timelineCollapsed ? 'hidden' : ''}`} onClick={(e) => e.stopPropagation()}>
                            <Timeline
                                currentPolicy={item}
                            />
                        </div>
                        {!jieduCollapsed && <div className={`bg-[#fafafa] rounded-box`} onClick={(e) => e.stopPropagation()}>
                        </div>}
                    </>
            }
        </>
    )
}